<template>
  <v-row no-gutters>
    <v-col cols="12" lg="10" xl="5" class="pa-3">
      <h2 class="proxima--font font-weight-bold">Shop Settings</h2>
      <transition name="fade-fast" mode="out-in">
        <div
          v-if="loading || isClearingSettings"
          class="text-center grey--text text--darken-2 mt-4"
        >
          <h3 class="mt-2">{{
            isClearingSettings ? 'Clearing Sensitive Data' : 'Loading Settings'
          }}</h3>
          <v-progress-circular
            indeterminate
            class="mt-4"
            color="grey"
            size="20"
            width="2"
          />
        </div>

        <div key="settings" v-else>
          <div class="settings-form pt-2">
            <v-divider class="mb-2" />
            <ShopSettingsFieldList
              :fields="getVisibleSettings"
              @save="saveSettings"
            />
          </div>
        </div>
      </transition>
    </v-col>

    <transition name="fade">
      <AdminButtonPublishChanges v-if="isDirty" @click="saveSettings" />
    </transition>

    <v-snackbar
      v-model="showLinkCopiedMessage"
      :timeout="2000"
      color="blue darken-2"
    >
      <h3 class="text-center">
        Link Copied to the Clipboard
      </h3>
    </v-snackbar>
    <v-snackbar
      v-model="showSubscriptionSnackbar"
      :timeout="5000"
      color="blue darken-2"
      :vertical="wasStripeSubscribeSuccessful"
    >
      <template v-if="wasStripeSubscribeSuccessful">
        <h1>Thank you! 🎉</h1>
        <h3 class="py-3">
          Your subscription has been activated.
        </h3>
      </template>
      <h3 v-else class="text-center">You exited the Stripe checkout page.</h3>
    </v-snackbar>
  </v-row>
</template>

<script>
import _get from 'lodash/get'
import { mapGetters, mapActions } from 'vuex'
import ShopSettingsFieldList from '@/components/ShopSettingsFieldList'
import AdminButtonPublishChanges from '@/components/AdminButtonPublishChanges'
import ShopModel from '@/models/ShopModel'

export default {
  name: 'ManageShopSettings',
  components: {
    ShopSettingsFieldList,
    AdminButtonPublishChanges,
  },
  meta() {
    return { title: `Shop Settings | Minimart` }
  },
  data: () => {
    return {
      originalSettings: {},
      hoveringSection: null,
      canCopy: false,
      showLinkCopiedMessage: false,
      showSubscriptionSnackbar: false,
      loading: true,
      error: false,
      isClearingSettings: false,
      subscriptionPoll: null,
      subscriptionPollCounter: 0,
    }
  },
  computed: {
    ...mapGetters('admin', ['getSaving']),
    ...mapGetters('shop', [
      'getShop',
      'getShopId',
      'getDirtyFields',
      'getVisibleSettings',
    ]),
    isDirty() {
      let dirtScore = 0
      Object.keys(this.getDirtyFields).forEach(
        (fieldName) =>
          (dirtScore +=
            this.originalSettings[fieldName] != this.getDirtyFields[fieldName])
      )
      return !!dirtScore
    },
    wasStripeSubscribeSuccessful() {
      let subResponse = _get(this.$route.query, 'sub', 'canceled')
      return subResponse === 'success'
    },
  },
  methods: {
    ...mapActions('shop', [
      'fetchShopSettings',
      'clearSettingsFromState',
      'saveAllSettings',
    ]),

    async saveSettings() {
      this.error = false
      try {
        await this.saveAllSettings()
        this.originalSettings = { ...(await this.getShop) }
      } catch (error) {
        this.error = true
      }
    }, // saveSettings

    async copyLink(hash) {
      let link = `${document.location.origin}${this.$route.path}${hash}`
      await navigator.clipboard.writeText(link)
      this.showLinkCopiedMessage = true
    },
    reloadPage() {
      let href = window.location.href
      window.location.href = href
    }, // reloadPage

    async leaveRoute(next) {
      this.isClearingSettings = true
      await this.clearSettingsFromState()
      next()
    },
    initSubscriptionPoll() {
      this.subscriptionPoll = setInterval(async () => {
        this.subscriptionPollCounter++
        try {
          await ShopModel.fetchById(this.getShopId)
          let { is_subscribed } = await ShopModel.find(this.getShopId)
          if (is_subscribed || this.subscriptionPollCounter >= 20) {
            this.clearSubscriptionPoll()
          }
        } catch (error) {
          this.clearSubscriptionPoll()
          return Promise.reject(error)
        }
      }, 1000)
    }, // initSubscriptionPoll

    clearSubscriptionPoll() {
      if (this.subscriptionPoll) {
        clearInterval(this.subscriptionPoll)
      }
    }, // clearSubscriptionPoll
  }, // methods
  async mounted() {
    let cloudinaryLib = await document.createElement('script')
    await cloudinaryLib.setAttribute(
      'src',
      'https://widget.cloudinary.com/v2.0/global/all.js'
    )
    await document.head.appendChild(cloudinaryLib)
    this.canCopy = !!navigator.clipboard
  },
  async created() {
    this.loading = true
    this.error = false
    this.isClearingSettings = false
    try {
      await this.fetchShopSettings()
      this.originalSettings = { ...(await this.getShop) }
    } catch (error) {
      this.error = true
    } finally {
      this.loading = false
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty) {
      const answer = window.confirm(
        'Your changes have not been published yet. Do you want to discard them and leave this page?'
      )
      if (answer) {
        this.leaveRoute(next)
      } else {
        next(false)
      }
    } else {
      this.leaveRoute(next)
    }
  },
  beforeDestroy() {
    this.clearSubscriptionPoll()
  },
  watch: {
    wasStripeSubscribeSuccessful: {
      handler() {
        if (typeof this.$route.query.sub === 'undefined') {
          return
        }
        this.showSubscriptionSnackbar = true
        // remove the query param without Vue detecting a page navigation/reload
        history.pushState({}, null, this.$route.path)
        // remove the query param from $route so this only triggers once
        delete this.$route.query.sub
        setTimeout(() => {
          this.$scrollTo('#billing', 700, { offset: -65 })
        }, 1300)

        let { is_subscribed } = ShopModel.find(this.getShopId)
        if (!is_subscribed) {
          this.initSubscriptionPoll()
        }
      },
      immediate: true,
    },
  },
}
</script>

<style lang="scss" scoped>
.publish-btn.theme--light.v-btn {
  z-index: 5;
  &.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
    background: rgba(225, 225, 225, 1) !important;
  }
}
ul.toc {
  list-style-type: none;
  padding-left: 0;
  li {
    padding-left: 0px;
    a {
      text-decoration: none;
    }
  }
}
.settings-form {
  position: relative;
}
</style>
